import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    Box, Typography, Paper, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, MenuItem, Select, FormControl,
    InputLabel, Grid, Card, Stack, TextField, IconButton, Chip, CircularProgress, Avatar
} from "@mui/material";
import {
    PhotoCamera, History, Delete, Save, Close, EventBusy,  
} from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, query, where } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../firebase";
import { format, differenceInMinutes, parseISO, isAfter, setHours, setMinutes } from "date-fns";
   

function Staffattendance() {
    const legendItems = [
        { label: "Present", color: "#e8f5e9", border: "#a5d6a7" },
        { label: "Late Mark", color: "#fff3e0", border: "#ffcc80" },
        { label: "Half Day", color: "#e3f2fd", border: "#bbdefb" },
        { label: "Absent", color: "#ffebee", border: "#ef9a9a" },
        { label: "Holiday", color: "#f5f5f5", border: "#e0e0e0" },
    ];

    const [staffList, setStaffList] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [attendanceData, setAttendanceData] = useState({});
    const [viewDate, setViewDate] = useState(new Date());
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewImg, setPreviewImg] = useState(null);



    // Form States
    const [selectedDate, setSelectedDate] = useState(null);
    const [status, setStatus] = useState("Present");
    const [checkInTime, setCheckInTime] = useState("09:00");
    const [checkOutTime, setCheckOutTime] = useState("18:00");

    const [checkInImg, setCheckInImg] = useState("");
    const [checkInFile, setCheckInFile] = useState(null);
    const [checkOutImg, setCheckOutImg] = useState("");
    const [checkOutFile, setCheckOutFile] = useState(null);

    const [counts, setCounts] = useState({ present: 0, absent: 0, halfDay: 0, late: 0, salaryEarned: 0 });

    useEffect(() => { loadStaff(); loadAttendance(); }, []);

    useEffect(() => {
        if (selectedStaff) calculateMonthlyStats(viewDate);
    }, [attendanceData, selectedStaff, viewDate]);

    const loadStaff = async () => {
        const q = query(collection(db, "salaryAllocations"));
        const snap = await getDocs(q);
        setStaffList(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const loadAttendance = async () => {
        const snapshot = await getDocs(collection(db, "staffattendance"));
        let map = {};
        snapshot.docs.forEach(d => {
            const data = d.data();
            const key = `${data.staffId}_${data.date}`;
            map[key] = { docId: d.id, ...data };
        });
        setAttendanceData(map);
    };

    const handleDateClick = (date) => {
        if (!selectedStaff) return;
        const dStr = format(date, "yyyy-MM-dd");
        const existing = attendanceData[`${selectedStaff.id}_${dStr}`];

        setSelectedDate(date);
        setStatus(existing?.status || "Present");
        setCheckInImg(existing?.checkInImage || "");
        setCheckOutImg(existing?.checkOutImage || "");
        setCheckInTime(existing?.checkInTime ? format(new Date(existing.checkInTime), "HH:mm") : "09:00");
        setCheckOutTime(existing?.checkOutTime ? format(new Date(existing.checkOutTime), "HH:mm") : "18:00");
        setOpen(true);
    };

    const handleSave = async (customStatus = null) => {
        if (!selectedDate || !selectedStaff) return;
        setLoading(true);

        try {
            const dStr = format(selectedDate, "yyyy-MM-dd");
            const docId = `${selectedStaff.id}_${dStr}`;

            const getFullISO = (timeStr) => {
                const [h, m] = timeStr.split(':');
                const d = new Date(selectedDate);
                d.setHours(parseInt(h), parseInt(m), 0, 0);
                return d.toISOString();
            };

            const isoIn = getFullISO(checkInTime);
            const isoOut = getFullISO(checkOutTime);

            // Auto-Status Logic (if not explicitly a holiday)
            // inside handleSave...
            let finalStatus = customStatus || status;

            // If user is just saving without a specific "Holiday" click
            if (!customStatus) {
                const checkInDate = new Date(getFullISO(checkInTime));
                const checkOutDate = new Date(getFullISO(checkOutTime));

                // Auto-calculate only if the current selection is "Present" or "Late Mark"
                // This prevents overwriting if the user manually clicked "Half Day"
                if (status === "Present" || status === "Late Mark") {
                    const lateLimit = setMinutes(setHours(new Date(selectedDate), 9), 30);
                    const workMins = differenceInMinutes(checkOutDate, checkInDate);

                    if (workMins > 0 && workMins < 240) {
                        finalStatus = "Half Day";
                    } else if (isAfter(checkInDate, lateLimit)) {
                        finalStatus = "Late Mark";
                    } else {
                        finalStatus = "Present";
                    }
                }
            }

            let finalInUrl = checkInImg, finalOutUrl = checkOutImg;
            if (checkInFile) {
                const refIn = ref(storage, `attendance/${docId}_in.jpg`);
                await uploadBytes(refIn, checkInFile);
                finalInUrl = await getDownloadURL(refIn);
            }
            if (checkOutFile) {
                const refOut = ref(storage, `attendance/${docId}_out.jpg`);
                await uploadBytes(refOut, checkOutFile);
                finalOutUrl = await getDownloadURL(refOut);
            }

            await setDoc(doc(db, "staffattendance", docId), {
                staffId: selectedStaff.id,
                staffName: selectedStaff.staffName,
                date: dStr,
                status: finalStatus,
                checkInTime: isoIn,
                checkOutTime: isoOut,
                checkInImage: finalInUrl,
                checkOutImage: finalOutUrl,
                updatedAt: new Date().toISOString()
            }, { merge: true });

            await loadAttendance();
            setOpen(false);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    const calculateMonthlyStats = (activeDate) => {
        const monthStr = format(activeDate, "yyyy-MM");
        let p = 0, a = 0, h = 0, l = 0;
        Object.values(attendanceData).forEach(r => {
            if (r.staffId === selectedStaff.id && r.date.startsWith(monthStr)) {
                if (r.status === "Present") p++;
                else if (r.status === "Absent") a++;
                else if (r.status === "Half Day") h++;
                else if (r.status === "Late Mark") l++;
            }
        });
        const basic = Number(selectedStaff.totalSalary) || 0;
        const perDay = basic / 30;
        const earned = ((p + l) * perDay) + (h * 0.5 * perDay);
        setCounts({ present: p, absent: a, halfDay: h, late: l, salaryEarned: Math.round(earned) });
    };

    return (
        <Box p={1}>
            <Grid className="row" spacing={3}>
                <Grid item xs={12} className="col-sm-3">
                    <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap justifyContent="center">
                        {legendItems.map((item) => (
                            <Stack
                                key={item.label}
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                sx={{
                                    px: 1.5, py: 0.5, borderRadius: 10,
                                    bgcolor: item.color, border: `1px solid ${item.border}`
                                }}
                            >
                                {item.icon}
                                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                                    {item.label}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                    <Stack spacing={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Select Staff Member</InputLabel>
                            <Select
                                value={selectedStaff?.id || ""}
                                label="Select Staff Member"
                                onChange={(e) => setSelectedStaff(staffList.find(s => s.id === e.target.value))}
                            >
                                {staffList.map(s => <MenuItem key={s.id} value={s.id}>{s.staffName}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <Card sx={{ p: 2, bgcolor: '#1a237e', color: 'white', borderRadius: 4, textAlign: 'center' }}>
                            <Typography variant="caption">ESTIMATED EARNINGS</Typography>
                            <Typography variant="h4" fontWeight="bold">₹{counts.salaryEarned}</Typography>
                            <Stack direction="row" justifyContent="space-around" mt={1}>
                                <Typography variant="caption">P: {counts.present + counts.late}</Typography>
                                <Typography variant="caption">H: {counts.halfDay}</Typography>
                                <Typography variant="caption">A: {counts.absent}</Typography>
                            </Stack>
                        </Card>

                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 4, maxHeight: 400, overflow: 'auto' }}>
                            <Typography variant="subtitle2" mb={2} fontWeight="bold">Recent Records</Typography>
                            {Object.values(attendanceData)
                                .filter(r => r.staffId === selectedStaff?.id)
                                .sort((a, b) => b.date.localeCompare(a.date)).map((log, i) => (
                                    <Box key={i} sx={{ mb: 1, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="caption" fontWeight="bold">{log.date}</Typography>
                                            <Chip size="small" label={log.status} color={log.status === "Present" ? "success" : log.status === "Late Mark" ? "warning" : "default"} />
                                        </Stack>
                                    </Box>
                                ))}
                        </Paper>
                    </Stack>
                </Grid>

                <Grid item xs={12} className="col-sm-9">
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 4 }}>
                        {selectedStaff ? (
                            <Calendar
                                onClickDay={handleDateClick}
                                tileClassName={({ date, view }) => {
                                    if (view !== 'month') return;
                                    const res = attendanceData[`${selectedStaff.id}_${format(date, "yyyy-MM-dd")}`]?.status;
                                    if (res === "Present") return "present-tile";
                                    if (res === "Absent") return "absent-tile";
                                    if (res === "Half Day") return "halfday-tile";
                                    if (res === "Late Mark") return "late-tile";
                                    if (res === "Holiday") return "holiday-tile";
                                }}
                                onActiveStartDateChange={({ activeStartDate }) => setViewDate(activeStartDate)}
                            />
                        ) : <Box sx={{ p: 10, textAlign: 'center' }}>Select staff to view attendance</Box>}
                    </Paper>
                </Grid>
            </Grid>

            {/* Entry Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Attendance: {format(selectedDate || new Date(), "dd MMM")}</DialogTitle>
                <DialogContent dividers>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {["Present", "Absent", "Half Day", "Late Mark"].map(s => (
                            <Button
                                key={s}
                                variant={status === s ? "contained" : "outlined"}
                                onClick={() => setStatus(s)}
                                size="small"
                                sx={{
                                    flex: '1 1 45%', // Allows 2 buttons per row on small screens
                                    color: s === "Late Mark" && status !== s ? "#ed6c02" : undefined,
                                    borderColor: s === "Late Mark" && status !== s ? "#ed6c02" : undefined,
                                }}
                            >
                                {s}
                            </Button>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={() => handleSave()} disabled={loading}>
                        {loading ? "Saving..." : "Save Record"}
                    </Button>
                </DialogActions>
            </Dialog>

            <style>{`
    .react-calendar { width: 100% !important; border: none !important; }
    .react-calendar__tile { 
        height: 80px !important; 
        border-radius: 8px !important; 
        transition: 0.2s; 
        border: 1px solid transparent !important; 
    }

    /* Present - Green */
    .present-tile { background: #e8f5e9 !important; color: #2e7d32 !important; border-color: #a5d6a7 !important; }

    /* Absent - Red */
    .absent-tile { background: #ffebee !important; color: #d32f2f !important; border-color: #ef9a9a !important; }

    /* Half Day - BLUE */
    .halfday-tile { 
        background: #e3f2fd !important; 
        color: #1976d2 !important; 
        border-color: #bbdefb !important; 
        font-weight: bold;
    }

    /* Late Mark - ORANGE */
    .late-tile { 
        background: #fff3e0 !important; 
        color: #ed6c02 !important; 
        border-color: #ffcc80 !important; 
        font-weight: bold;
    }

    .holiday-tile { background: #f5f5f5 !important; color: #9e9e9e !important; border-color: #e0e0e0 !important; opacity: 0.6; }
    
    /* Hover effect for all tiles */
    .react-calendar__tile:hover { background: #f0f0f0 !important; transform: scale(1.02); z-index: 1; }
`}</style>
        </Box>
    );
}

export default Staffattendance;